import { pqSections } from './pqSections.js';
import { sectionProgress } from '../store/completion.js';

// The PQ as four pages instead of one long scroll (Joe Anderson, 10/21; Jimmy,
// 10/27). Newer advisors found ten sections in a row overwhelming, and the old
// paper PQ was pages too: balance sheet, income statement, personal details.
// Each tab holds whole sections, so nothing about a section changes, only
// which ones are on screen together.
//
// The order of `sections` inside a tab, and of the tabs themselves, is the
// order an advisor fills them in. The numbering the section headers show runs
// across the tabs (01-02 on the first, 03-04 on the second, ...), so a section
// keeps one number wherever it is mentioned.
//
// Household & Family is last (Joe Anderson, 10/29): the meeting opens on the
// money, and names, dates of birth and contact details are gathered at the end,
// the way the 10/21 review said asking a date of birth first was a cold start.
export const PQ_TABS = [
  {
    id: 'balance',
    label: 'Assets & Liabilities',
    short: 'Assets',
    sections: ['assets', 'liabilities'],
  },
  {
    id: 'cashflow',
    label: 'Income, Taxes & Expenses',
    short: 'Income',
    sections: ['income', 'taxesExpenses'],
  },
  {
    id: 'protection',
    label: 'Insurance & Estate Plan',
    short: 'Insurance',
    sections: ['insurance', 'goals'],
  },
  {
    id: 'household',
    label: 'Household & Family',
    short: 'Household',
    sections: ['family', 'contact', 'employment', 'relationships'],
  },
];

export const DEFAULT_TAB = PQ_TABS[0].id;

// Map each section to a tone color for the left accent stripe and the section
// number. Lived in SideNav before the tabs; the nav is gone, the tones stay.
export const TONE_BY_SECTION = {
  family: 'indigo',
  contact: 'indigo',
  employment: 'teal',
  goals: 'violet',
  relationships: 'violet',
  assets: 'cyan',
  liabilities: 'rose',
  insurance: 'amber',
  income: 'teal',
  taxesExpenses: 'rose',
};

const SECTION_BY_ID = Object.fromEntries(pqSections.map(s => [s.id, s]));

// Section number as the header prints it ("05"), by section id.
export const SECTION_NUMBERS = (() => {
  const out = {};
  let n = 0;
  PQ_TABS.forEach(t => t.sections.forEach(id => { out[id] = String(++n).padStart(2, '0'); }));
  return out;
})();

const TAB_BY_SECTION = (() => {
  const out = {};
  PQ_TABS.forEach(t => t.sections.forEach(id => { out[id] = t.id; }));
  return out;
})();

export function tabById(id) {
  return PQ_TABS.find(t => t.id === id) || PQ_TABS[0];
}

// The tab a section lives on, or null for an id that is not a section.
export function tabForSection(sectionId) {
  return TAB_BY_SECTION[sectionId] || null;
}

// The section objects of a tab, in fill order.
export function tabSections(tab) {
  return tab.sections.map(id => SECTION_BY_ID[id]).filter(Boolean);
}

// A tab's completion is the mean of its sections', the same figure each
// section header shows, so the tab and its sections never disagree.
export function tabProgress(tab, data) {
  const secs = tabSections(tab);
  if (!secs.length) return 0;
  const sum = secs.reduce((acc, s) => acc + sectionProgress(s, data), 0);
  return Math.round(sum / secs.length);
}

// Previous and next tab in fill order, for the footer links. Null at the ends.
export function adjacentTabs(tabId) {
  const i = PQ_TABS.findIndex(t => t.id === tabId);
  return {
    prev: i > 0 ? PQ_TABS[i - 1] : null,
    next: i >= 0 && i < PQ_TABS.length - 1 ? PQ_TABS[i + 1] : null,
  };
}
